import { action, observable, type IObservableArray } from 'mobx';

/**
 * The shared owner of a one-to-many parent→children relationship between two
 * editable entities. Both sides hold the *same* `Aggregate` instance and route
 * their public relationship members through it, so neither side has to expose
 * the mutating internals (an `add`/`remove`/`update` on the parent's public
 * surface) that the relationship would otherwise leak.
 *
 * - The parent constructs the aggregate, seeds it (`append`) from loaded data,
 *   and redirects its public members to it:
 *   ```ts
 *   private readonly _recurrences = new Aggregate<Definition, Recurrence>(
 *     this,
 *     (agg) => new Recurrence(agg),
 *   );
 *   get recurrences(): readonly Recurrence[] { return this._recurrences.children; }
 *   createRecurrence = (): Recurrence => this._recurrences.create();
 *   ```
 * - Each child receives the aggregate at construction and redirects too:
 *   ```ts
 *   constructor(private readonly _aggregate: Aggregate<Definition, Recurrence>) {}
 *   get recurrenceDefinition(): Definition { return this._aggregate.parent; }
 *   delete = (): void => this._aggregate.remove(this);
 *   ```
 *
 * `children` is backed by a non-deep observable array: appends/removes notify
 * (length and membership are reactive), while each child manages its own field
 * reactivity. Removal is by identity, matching how a child deletes itself
 * (`remove(this)`) — never by a positional index threaded down from a view.
 */
export class Aggregate<P, C> {
  readonly parent: P;
  private readonly _create: (aggregate: Aggregate<P, C>) => C;
  private readonly _children: IObservableArray<C>;

  /**
   * @param parent the owning entity, exposed to children as their back-reference.
   * @param create factory for a fresh (empty) child, wired to this aggregate.
   *   Used by `create()`; loaded children are seeded via `append`.
   */
  constructor(parent: P, create: (aggregate: Aggregate<P, C>) => C) {
    this.parent = parent;
    this._create = create;
    this._children = observable.array<C>([], { deep: false });
  }

  get children(): readonly C[] {
    return this._children;
  }

  /** Append an already-built child (used to seed from loaded data). */
  append = (child: C): C => {
    this._children.push(child);
    return child;
  };

  /** Build a fresh child via the factory, append it, and return it. */
  create = (): C => this.append(this._create(this));

  /** Remove a child by identity. No-op if it isn't present. */
  remove = (child: C): void => {
    this._children.remove(child);
  };

  /**
   * Reorder a child: move the one currently at `fromIndex` so it ends up at
   * `toIndex`, shifting the others to close the gap and open the slot. Both are
   * positions in the *current* order, and `toIndex` is the moved child's **final
   * resting index** — after the move `children[toIndex]` is the moved child (no
   * off-by-one to apply at the call site). This matches react-beautiful-dnd's
   * `source.index` / `destination.index`, and is the same contract Move-up /
   * Move-down buttons use:
   *
   *   move(i, i - 1); // move up
   *   move(i, i + 1); // move down
   *
   * A no-op when the indices are equal or out of range. Wrapped in `action` so
   * the remove + insert is a single transaction — observers never see the
   * intermediate (briefly-removed) state, only the final order.
   */
  move = action((fromIndex: number, toIndex: number): void => {
    if (fromIndex === toIndex) {
      return;
    }
    if (fromIndex < 0 || fromIndex >= this._children.length || toIndex < 0 || toIndex >= this._children.length) {
      return;
    }
    const [child] = this._children.splice(fromIndex, 1);
    this._children.splice(toIndex, 0, child);
  });

  /**
   * Empty the collection in place. Lets an owner rebuild its children on
   * reload without replacing the aggregate instance — children and observers
   * keep tracking the same array, so reactivity survives the swap.
   */
  clear = (): void => {
    this._children.clear();
  };
}
